import React, { FC, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import { FeatureFlag } from 'app/api/services/site-configs/types';

import { API, useFetch } from 'app/api';
import { SsrModule } from 'app/constants/ssrModules';
import { Skeleton } from 'app/design-system/components/Skeleton';
import { useTranslation } from 'app/lang';
import { useFeatureFlag } from 'app/lib/feature-flags';
import { useComponentWillReceiveProps } from 'app/packages/lib/hooks';

import { Content } from './components/Content';

import styles from './MiniBanners.module.scss';

const BANNERS_NUMBER = 2;

type Props = {
  className?: string;
};

export const MiniBanners: FC<Props> = ({ className }) => {
  const { lang } = useTranslation();

  const rootClassName = classNames(styles.root, className);
  const isActiveBannersPreview = useFeatureFlag(FeatureFlag.BANNERS_PREVIEW);

  const { data, loading, requested, makeRequest } = useFetch(
    API.strapiServices.banners.getBanners(isActiveBannersPreview, 'minibanner'),
    { ssrUniqKey: SsrModule.MINI_BANNERS },
  );

  useEffect(() => {
    if (!requested) {
      makeRequest();
    }
  }, []);

  useComponentWillReceiveProps(() => {
    makeRequest();
  }, [lang]);

  const banners = useMemo(() => {
    return data?.sort((current, next) => current.weight + next.weight).slice(0, BANNERS_NUMBER);
  }, [data]);

  if (loading) {
    return (
      <>
        <Skeleton width="100%"
                  height={160} />
        <Skeleton width="100%"
                  height={160} />
      </>
    );
  }

  if (!banners || banners.length < BANNERS_NUMBER) {
    return null;
  }

  return (
    <ul className={rootClassName}
        data-ssr-id={SsrModule.MINI_BANNERS}>
      {banners.map(({ id, title, description, image, link }) => {
        const style = image ? { backgroundImage: `url(${image})` } : {};
        return (
          <li key={id}>
            {link ? (
              <Link to={link}
                    className={styles.item}
                    style={style}>
                <Content title={title}
                         description={description} />
              </Link>
            ) : (
              <a className={styles.item}
                 style={style}>
                <Content title={title}
                         description={description} />
              </a>
            )}
          </li>
        );
      })}
    </ul>
  );
};
